import React, { ReactNode, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import makeStyles from '@mui/styles/makeStyles';
import { theme } from '../../../../theme';
import {
  Box,
  CardContent,
  Divider,
  Grid,
  LinearProgress,
  List,
  ListItem,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip,
  Collapse,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { CREATE_CREDIT_TIERS_RESULTS, GET_SCORECARD } from '../queries';
import {
  ApplicantData,
  Conditions,
  ScoreBand,
  ScoreBreakdownItem,
  ScorecardEngine,
  ScoreResult,
} from '@trnsact/trnsact-scorecard-sdk';
import _, { cond } from 'lodash';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { useMutation, useQuery } from '@apollo/client';
import { CorporateGuarantorModal } from 'modules/creditApplicationDetailsActions';
import { ContentViewType, ModalsKeys } from 'global';
import { portalConfigurationsSelectors } from 'redux/portalConfigurationReducer';
import { ScorecardCriteria } from '../types';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import { LenderWaterfallPrescreenCriteriaResultType } from '@trnsact/trnsact-shared-types';
import Tile from 'components/Tile/Tile';
import { formatCurrency, formatNumberWithDelimiter } from 'utils';

interface Props {
  vo: any;
  refetchVO: any;
  vp: any;
  account: any;
  equipmentData: any;
  refetchEquipment: any;
  equipmentLoading: any;
  userProfile: any;
  refetchCreditApp: any;
  creditApplication: any;
  portalConfigurationDataLoading: any;
  creditAppOwners: any[];
  cs: any;
  refetchCreditSubmission?: any;
  contentViewType?: ContentViewType;
}

const getFicoScoreFromCbrs = (cbrs: any[]): number => {
  try {
    const ficoReport = cbrs.find(cbr => {
      return cbr && cbr.bureauType === 'FICO';
    });
    if (ficoReport) {
      console.info(`Found FICO Report`);
      const ficoField = ficoReport.fields?.find((f: any) => {
        return f?.key === 'FICO Score';
      });
      if (ficoField && ficoField.value) {
        return parseInt(ficoField.value, 10);
      }
    }
    console.warn('Could not find FICO score... returning score of 0 (not available)');
    return 0;
  } catch (e: any) {
    console.error(`Error getting FICO Score... returning score of 0: ${e.message}`);
    return 0;
  }
};
export const ScoreCardV2 = ({
  vo,
  refetchVO,
  refetchCreditSubmission,
  vp,
  account,
  equipmentData,
  refetchEquipment,
  equipmentLoading,
  refetchCreditApp,
  cs,
  creditApplication,
  portalConfigurationDataLoading,
  contentViewType = ContentViewType.Desktop,
  creditAppOwners,
}: Props) => {
  const APPLICANT_OPEN_STATE_KEY: string = 'applicant';
  const [createCreditTiersResults, { data, loading, error }] = useMutation(CREATE_CREDIT_TIERS_RESULTS);

  const handleCreateResults = async (inputArray: any) => {
    try {
      const response = await createCreditTiersResults({ variables: { input: inputArray } });
      // handle response.data.createCreditTiersResults as needed
    } catch (err) {
      // handle error
      console.log(error);
      debugger;
    }
  };
  const classes = useStyles();
  const history = useHistory();
  const [scorecardCriteria, setScorecardCriteria] = useState<ScorecardCriteria | null>(null);
  const [financeProgramId, setFinanceProgramId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [totalScore, setTotalScore] = useState<number>(0);
  const [band, setBand] = useState<string>('');
  const [bandColor, setBandColor] = useState<string>('#FFF');
  const [breakDown, setBreakDown] = useState<ScoreBreakdownItem[]>([]);
  const [engine, setEngine] = useState<ScorecardEngine | null>(null);
  const [results, setResults] = useState<
    | (ScoreResult & {
        band?: string;
        bandColor?: string;
      })
    | null
  >(null);
  const [applicantData, setApplicantData] = useState<ApplicantData | null>(null);
  const [listOfFacts, setListOfFacts] = useState<Map<string, { fact: string; type: string }>>(new Map());
  // Manually-entered fields state
  const [manualFields, setManualFields] = useState<{ [key: string]: any }>({});
  // Only allow editing fields that are part of ApplicantData
  // Define the allowed keys and their labels
  const applicantFieldDefs: { key: keyof ApplicantData; label: string }[] = [
    { key: 'AVERAGE_BANK_BALANCE', label: 'Average Bank Balance' },
    { key: 'INDUSTRY_EXPERIENCE_YEARS', label: 'Industry Experience Years' },
    { key: 'HOME_OWNERSHIP', label: 'Home Ownership' },
    { key: 'IS_REPEAT_BORROWER', label: 'Is Repeat Borrower' },
    { key: 'NUMBER_OF_TRUCKS', label: 'Number of Trucks' },
    { key: 'EQUIPMENT_MODEL_YEAR', label: 'Equipment Model Year' },
    { key: 'YEARS_IN_BUSINESS', label: 'Years in Business' },
    { key: 'PERSONAL_CREDIT_SCORE', label: 'Personal Credit Score' },
  ];

  // Only show fields that are in ApplicantData or have been manually entered
  const allApplicantKeys = React.useMemo(() => {
    const keys = applicantFieldDefs.map(f => f.key);
    // If a user has entered a value for a field not in the default list, include it
    Object.keys(manualFields).forEach(k => {
      if (!keys.includes(k as keyof ApplicantData)) keys.push(k as keyof ApplicantData);
    });
    return keys;
  }, [manualFields]);
  const { data: scorecardData, refetch: refetchScorecard } = useQuery(GET_SCORECARD, {
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      setScorecardCriteria(data.scoreCard[0]?.prescreenCriteria);

      setFinanceProgramId(data.scoreCard[0]?.financeProgramId);
      setIsLoading(false);
    },
  });

  const currentUserProfile = useSelector((state: any) => state.userProfile);

  useEffect(() => {
    setIsLoading(!creditApplication || !vo);
  }, [creditApplication, vo, portalConfigurationDataLoading]);

  const typeInference = (v: any): string => {
    let inferredType: string;
    if (typeof v === 'boolean') {
      inferredType = 'boolean';
    } else if (typeof v === 'number') {
      inferredType = 'number';
    } else if (typeof v === 'string') {
      if (v === 'true' || v === 'false') {
        inferredType = 'boolean';
      } else if (v.trim() !== '' && !isNaN(Number(v))) {
        inferredType = 'number';
      } else {
        inferredType = 'string';
      }
    } else {
      inferredType = typeof v;
    }

    return inferredType;
  };
  useEffect(() => {
    if (!isLoading && scorecardCriteria) {
      let ficoScore = _.get(creditApplication, 'creditApplicationOwner[0].personalGuarantor.mostRecentCbrs', undefined);
      if (!_.isEmpty(ficoScore)) {
        ficoScore = getFicoScoreFromCbrs(ficoScore);
      }

      let eqModelYear = undefined;

      if (!_.isEmpty(_.get(equipmentData, 'equipments[0].year'))) {
        eqModelYear = Number(_.get(equipmentData, 'equipments[0].year'));
      }

      let applicant: ApplicantData = {
        AVERAGE_BANK_BALANCE: _.get(creditApplication, 'primaryBankAverageBalance', undefined),
        INDUSTRY_EXPERIENCE_YEARS: creditApplication.industryExpYears,
        HOME_OWNERSHIP: _.get(creditApplication, 'creditApplicationOwner.homeType', undefined),
        IS_REPEAT_BORROWER: creditApplication.previousFinanceExp
          ? creditApplication.previousFinanceExp === 'true'
            ? 'true'
            : 'false'
          : undefined,
        NUMBER_OF_TRUCKS: creditApplication.piecesOfTruckEqpt,
        EQUIPMENT_MODEL_YEAR: eqModelYear,
        YEARS_IN_BUSINESS: creditApplication.yearsInBusiness,
      };
      if (cs.scoreCardResult) {
        applicant = cs.scoreCardResult.runtimeInput;
      }
      if (!_.isEmpty(ficoScore)) {
        applicant = { ...applicant, PERSONAL_CREDIT_SCORE: ficoScore };
      }

      // Convert manualFields values to their correct types
      const parsedManualFields: { [key: string]: any } = {};
      Object.entries(manualFields).forEach(([k, v]) => {
        if (typeof v === 'string') {
          // Try to parse as number
          if (v.trim() !== '' && !isNaN(Number(v))) {
            parsedManualFields[k] = Number(v);
          } else if (v === 'true' || v === 'false') {
            parsedManualFields[k] = v === 'true';
          } else {
            parsedManualFields[k] = v;
          }
        } else {
          parsedManualFields[k] = v;
        }
      });

      // Merge parsed manual fields into applicant
      applicant = { ...applicant, ...parsedManualFields };
      console.log(applicant);

      // Use a Map to ensure unique entries per 'fact', inferring type from value
      const factsMap = new Map();
      scorecardCriteria.jsonCriteria.scoreCriteria.forEach((scoreCardRow: any) => {
        scoreCardRow?.jsonRules.conditions.all.forEach((cond: any) => {
          const v = cond.value;
          let inferredType: string = typeInference(v);
          factsMap.set(cond.fact, { fact: cond.fact, type: inferredType });
        });
        if (scoreCardRow && scoreCardRow?.customCriteria) {
          Object.values(scoreCardRow?.customCriteria).forEach((criteria: any) => {
            // If criteria.type is not set, try to infer from criteria.value
            let inferredType = criteria.type;

            factsMap.set(criteria.name, { fact: criteria.name, type: inferredType });
          });
        }
      });

      setListOfFacts(factsMap);

      setApplicantData(applicant);
    }
    // Only re-run when manualFields changes as well
  }, [isLoading, scorecardCriteria, manualFields, cs]);

  useEffect(() => {
    if (results) {
      setTotalScore(results.totalScore || 0);
      setBandColor(results.bandColor || '#FFF');
      setBand(results.band || '');
      setBreakDown(results.breakdown);
    }
  }, [results]);

  const handleReRunScoreCard = async () => {
    if (!scorecardCriteria) return;

    let multiCriteriaLayers: Array<{ conditions: { all: Array<any> } }> = [];
    scorecardCriteria.jsonCriteria.scoreCriteria.map(criterias => {
      let customCriteria: any = [];
      let presetCriteria: any = [];
      if (!_.isEmpty(criterias.customCriteria)) {
        customCriteria = Conditions.transformRuleObject(criterias.customCriteria).conditions.all;
      }
      if (!_.isEmpty(criterias.jsonRules)) {
        presetCriteria = criterias.jsonRules.conditions.all.map(condition => {
          let value = condition.value;
          if (typeof value === 'string') {
            if (value.trim() !== '' && !isNaN(Number(value))) {
              value = Number(value);
            } else if (value === 'true' || value === 'false') {
              value = value === 'true';
            } else {
              value = value;
            }
          }

          return {
            ...condition,
            value,
          };
        });
      }
      // Ensure both are arrays before spreading
      multiCriteriaLayers.push({
        conditions: {
          all: [
            ...(Array.isArray(presetCriteria) ? presetCriteria : []),
            ...(Array.isArray(customCriteria) ? customCriteria : []),
          ],
        },
      });
    });

    const bands = scorecardCriteria.jsonCriteria.scoreTiers as ScoreBand[];
    const engine = new ScorecardEngine(multiCriteriaLayers, undefined, bands);
    setResults(await engine?.score(applicantData || {}));
  };

  const handleSaveScoreCard = async () => {
    // Prepare input for createCreditTiersResults mutation
    if (results) {
      const input = [
        {
          accountId: account?.id,
          vendorOpportunityId: vo?.vendorOpportunityId,
          processInstanceId: vo?.processInstanceId,
          prescreenCriteriaId: scorecardCriteria?.prescreenCriteriaId,
          creditSubmissionId: cs?.creditSubmissionId,
          financeProgramId,
          tiersType: 'SCORECARD',
          runtimeInput: applicantData,
          runtimeOutput: results,
          result: LenderWaterfallPrescreenCriteriaResultType.PASS,
          highestTierPassed: results.band || '',
          highestTierColor: results.bandColor || '',
        },
      ];
      setIsLoading(true);
      await handleCreateResults(input);
      await refetchCreditSubmission();
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (applicantData && financeProgramId) {
      handleReRunScoreCard();
    }
  }, [applicantData, financeProgramId]);

  return scorecardCriteria ? (
    <Tile title="Score Card" useCollapsing>
      <CardContainer
        title=""
        variant="outlined"
        contentViewType={contentViewType}
        actionBtnProps={
          !isLoading
            ? {
                label: 'Save',
                onClick: handleSaveScoreCard,
              }
            : undefined
        }
      >
        {!isLoading && (
          <>
            <Typography className={classes.cardHeaderDateOfApplication}>
              Score card results: {new Date().toLocaleDateString()}
            </Typography>
          </>
        )}
        <CardContent style={{ padding: 0 }}>
          {isLoading ? (
            <LinearProgress style={{ width: '100%' }} />
          ) : (
            <>
              {/* Score summary section */}
              <List component="nav" aria-labelledby="score-summary-list">
                <ListItem>
                  <Grid container alignItems="center">
                    <Grid item xs={6}>
                      <Typography variant="h6">Total Score</Typography>
                    </Grid>
                    <Grid item xs={6} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                      <Avatar sx={{ bgcolor: bandColor }} variant="square" style={{ width: 50, height: 50 }}>
                        {totalScore}
                      </Avatar>
                    </Grid>
                  </Grid>
                </ListItem>
                <ListItem>
                  <Grid container alignItems="center">
                    <Grid item xs={6}>
                      <Typography variant="subtitle1">Band:</Typography>
                    </Grid>
                    <Grid item xs={6} style={{ display: 'flex', alignItems: 'center' }}>
                      <Chip label={band} color={'default'} variant="outlined" />
                    </Grid>
                  </Grid>
                </ListItem>
                {breakDown && breakDown.length > 0 && (
                  <ListItem alignItems="flex-start" style={{ display: 'block' }}>
                    <Typography variant="subtitle1">Breakdown:</Typography>
                    <List dense sx={{ pl: 2 }}>
                      {Object.values(
                        breakDown.reduce(
                          (acc, item) => {
                            acc[item.category] = item;
                            return acc;
                          },
                          {} as Record<string, ScoreBreakdownItem>
                        )
                      ).map((item, idx) => (
                        <Collapse key={item.category} in={true} timeout="auto">
                          <ListItem alignItems="flex-start">
                            <ListItemIcon>
                              <CreditScoreIcon />
                            </ListItemIcon>
                            <ListItemText primary={item.category.replace(/_/g, ' ')} secondary={`+${item.score} pts`} />
                          </ListItem>
                        </Collapse>
                      ))}
                    </List>
                  </ListItem>
                )}
                <Divider />
              </List>

              <List component="nav" aria-labelledby="nested-list-subheader">
                <ListItem>
                  <RowNameText text="Input data:" />
                </ListItem>
                {Array.from(listOfFacts.values()).map((factObj: any) => {
                  const key = factObj.fact;
                  const label = key.replace(/_/g, ' ');
                  let value = manualFields[key] ?? (applicantData ? (applicantData as any)[key] : '');
                  // Type-based rendering using switch
                  let inputElement = null;
                  switch (factObj.type) {
                    case 'boolean':
                      inputElement = (
                        <Select
                          size="small"
                          value={value === true ? 'true' : value === false ? 'false' : ''}
                          onChange={e => {
                            setManualFields(f => ({ ...f, [key]: e.target.value === 'true' }));
                          }}
                          variant="outlined"
                          fullWidth
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>Please choose an option:</em>
                          </MenuItem>
                          <MenuItem value="true">Yes</MenuItem>
                          <MenuItem value="false">No</MenuItem>
                        </Select>
                      );
                      break;
                    case 'currency':
                      inputElement = (
                        <TextField
                          size="small"
                          value={
                            value !== '' && value !== null && value !== undefined
                              ? formatCurrency(value)
                              : (value ?? '')
                          }
                          onChange={e => {
                            let inputValue = e.target.value;

                            // Remove all non-numeric except dot and minus
                            const numericValue = inputValue.replace(/[^\d.-]/g, '');
                            setManualFields(f => ({
                              ...f,
                              [key]: numericValue === '' ? '' : Number(numericValue),
                            }));
                          }}
                          variant="outlined"
                          fullWidth
                        />
                      );
                      break;
                    case 'number':
                      inputElement = (
                        <TextField
                          size="small"
                          type="number"
                          value={value ?? ''}
                          onChange={e => {
                            const inputValue = e.target.value;
                            setManualFields(f => ({ ...f, [key]: inputValue === '' ? '' : Number(inputValue) }));
                          }}
                          variant="outlined"
                          fullWidth
                          inputProps={{ inputMode: 'decimal', pattern: '[0-9.]*' }}
                        />
                      );
                      break;
                    case 'text':
                    case 'string':
                      inputElement = (
                        <TextField
                          size="small"
                          value={value ?? ''}
                          onChange={e => {
                            setManualFields(f => ({ ...f, [key]: e.target.value }));
                          }}
                          variant="outlined"
                          fullWidth
                        />
                      );
                      break;
                    default:
                      // fallback to text
                      inputElement = (
                        <TextField
                          size="small"
                          value={value ?? ''}
                          onChange={e => {
                            setManualFields(f => ({ ...f, [key]: e.target.value }));
                          }}
                          variant="outlined"
                          fullWidth
                        />
                      );
                  }
                  return (
                    <ListItem key={key}>
                      <Grid container alignItems="center">
                        <Grid item xs={1} style={{ display: 'flex', justifyContent: 'center' }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: theme.palette.primary.main,
                              marginRight: 1,
                            }}
                          />
                        </Grid>
                        <Grid item xs={5}>
                          <RowNameText text={label} />
                        </Grid>
                        <Grid item xs={6}>
                          {inputElement}
                        </Grid>
                      </Grid>
                    </ListItem>
                  );
                })}
                <Divider />
              </List>
            </>
          )}
        </CardContent>
      </CardContainer>
    </Tile>
  ) : null;
};

const RowNameText = ({ text }: { text: string }) => {
  const classes = useStyles();
  return <Typography className={classes.listRowName}>{text || ''}</Typography>;
};

const RowValueText = ({ text, typographyProps }: { text: string; typographyProps?: any }) => {
  const classes = useStyles();
  return (
    <Typography className={classes.listRowValue} {...typographyProps}>
      {text || ''}
    </Typography>
  );
};

const useStyles = makeStyles(theme => ({
  link: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  listRowName: {
    color: '#666666',
    fontWeight: 500,
  },
  listRowValue: {
    color: '#212121',
  },
  card: {
    margin: 0,
    height: '100%',
    width: '100%',

    '& .MuiCardHeader-action': {
      margin: 0,
    },
    '& .MuiCardContent-root': {
      paddingTop: 0,
    },
  },
  cardHeader: {
    margin: '10px 10px 0 10px',
  },
  cardHeaderDateOfApplication: {
    display: 'flex',
    justifyContent: 'flex-end',
    color: '#666666',
  },
  recerteficationErrorIcon: {
    /*     margin: "0 8px 3px 0", */
    color: 'red',
    fontSize: 22,
    marginLeft: 5,
  },
}));
